import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiCall } from './client';
import type { Visit, PracticeData } from '../types';

// --- Practice data ---
export function usePractice() {
  return useQuery({
    queryKey: ['practice'],
    queryFn: () => apiCall<PracticeData>('/practices/mine'),
    staleTime: 60_000,
  });
}

// --- Visit data loading (mirrors loadAllVisits from visits-shared.js) ---
interface AllVisitsResult {
  activeVisits: Visit[];
  upcomingVisits: Visit[];
}

async function fetchAllVisits(hasActivePlan: boolean): Promise<AllVisitsResult> {
  const [roomsResult, scheduledResult, smsResult] = await Promise.allSettled([
    apiCall<{ rooms: Visit[] }>('/video/rooms'),
    apiCall<{ visits: Visit[] }>('/visits/upcoming'),
    hasActivePlan ? apiCall<{ visits: Visit[] }>('/sms/visits') : Promise.resolve({ visits: [] }),
  ]);

  let visits: Visit[] = [];
  let upcomingVisits: Visit[] = [];
  const seenIds = new Set<string>();

  if (roomsResult.status === 'fulfilled') {
    const rooms = (roomsResult.value.rooms || []).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    for (const r of rooms) {
      const id = r.roomId || r.visitId || '';
      if (id) seenIds.add(id);
      visits.push(r);
    }
  }

  const allScheduledVisits = scheduledResult.status === 'fulfilled' ? (scheduledResult.value.visits || []) : [];
  const oneHourFromNow = Date.now() + 60 * 60 * 1000;
  for (const v of allScheduledVisits) {
    const id = v.visitId || '';
    const alreadySeen = id && seenIds.has(id);

    if (!alreadySeen && (v.status === 'in-progress' || (v.scheduledAt && v.scheduledAt <= oneHourFromNow))) {
      if (id) seenIds.add(id);
      visits.push({ ...v, isScheduled: true });
    }
    if (v.scheduledAt && v.scheduledAt > Date.now()) {
      upcomingVisits.push(v);
    }
  }

  if (smsResult.status === 'fulfilled') {
    const smsItems = (smsResult.value.visits || []).map((v) => ({
      ...v,
      roomId: v.visitId,
      medium: 'sms' as const,
      isSmsVisit: true,
    }));
    visits.push(...smsItems);
  }

  visits.sort((a, b) => (a.scheduledAt || a.createdAt || 0) - (b.scheduledAt || b.createdAt || 0));
  upcomingVisits.sort((a, b) => (a.scheduledAt || 0) - (b.scheduledAt || 0));

  return { activeVisits: visits, upcomingVisits };
}

export function useAllVisits(hasActivePlan: boolean) {
  return useQuery({
    queryKey: ['allVisits'],
    queryFn: () => fetchAllVisits(hasActivePlan),
    refetchInterval: 60_000,
  });
}

export function usePastVisits() {
  return useQuery({
    queryKey: ['pastVisits'],
    queryFn: () => apiCall<{ visits: Visit[] }>('/visits/past'),
    select: (data) => data.visits || [],
  });
}

// --- Mutations ---
export function useStartInstantVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ medium, patientName, patientEmail, patientPhone, smsConsent }: {
      medium: string;
      patientName: string;
      patientEmail?: string;
      patientPhone?: string;
      smsConsent?: { source: string; consentedAt: number } | null;
    }) => {
      if (medium === 'phone') {
        return apiCall('/phone/start', { method: 'POST', body: { patientName, patientEmail, patientPhone } });
      } else if (medium === 'sms') {
        return apiCall('/sms/start', { method: 'POST', body: { patientName, patientPhone } });
      } else {
        const body: Record<string, unknown> = { patientName, patientEmail };
        if (patientPhone && smsConsent) {
          body.patientPhone = patientPhone;
          body.smsConsent = smsConsent;
        }
        return apiCall('/video/start', { method: 'POST', body });
      }
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['allVisits'] });
    },
  });
}

export function useScheduleVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiCall('/visits/schedule', { method: 'POST', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allVisits'] });
    },
  });
}

export function useEditVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ visit, body }: { visit: Visit; body: Record<string, unknown> }) => {
      if (visit.isScheduled || visit.scheduledAt) {
        return apiCall(`/visits/${visit.visitId}`, { method: 'PUT', body });
      } else if (visit.isSmsVisit) {
        return apiCall(`/sms/visits/${visit.visitId}`, { method: 'PUT', body });
      } else {
        return apiCall(`/video/rooms/${visit.roomId}`, { method: 'PUT', body });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allVisits'] });
    },
  });
}

export function useCancelVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (visit: Visit) => {
      if (visit.isScheduled || visit.scheduledAt) {
        return apiCall(`/visits/${visit.visitId}/cancel`, { method: 'POST' });
      } else {
        return apiCall(`/video/end/${visit.roomId}`, { method: 'POST' });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allVisits'] });
      queryClient.invalidateQueries({ queryKey: ['pastVisits'] });
    },
  });
}

export function useEndSmsVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (visitId: string) =>
      apiCall(`/sms/end/${visitId}`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allVisits'] });
    },
  });
}

export function useBillingSync() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiCall('/billing/sync', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practice'] });
    },
  });
}
